import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCustomProducts, deleteCustomProduct, exportCustomProducts } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Plus, MagnifyingGlass, Trash, PencilSimple, PuzzlePiece, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const statusConfig = {
    inquiry: { label: 'Inquiry', color: 'bg-blue-500/15 text-blue-600' },
    under_development: { label: 'In Development', color: 'bg-yellow-500/15 text-yellow-600' },
    sample_stage: { label: 'Sample Stage', color: 'bg-purple-500/15 text-purple-600' },
    quoted: { label: 'Quoted', color: 'bg-cyan-500/15 text-cyan-600' },
    approved: { label: 'Approved', color: 'bg-green-500/15 text-green-600' },
    converted_to_standard: { label: 'Converted', color: 'bg-emerald-600/15 text-emerald-700' },
    rejected: { label: 'Rejected', color: 'bg-red-500/15 text-red-600' },
    dormant: { label: 'Dormant', color: 'bg-slate-500/15 text-slate-600' },
};

const typeLabels = {
    modified_catalog: 'Modified Catalog',
    fully_new: 'Fully New',
    quote_only: 'Quote Only',
};

export default function CustomProductList() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const fetch = async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            const res = await getCustomProducts(params);
            setProducts(res.data.products);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetch(); }, [search, statusFilter]);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this custom product?')) return;
        try { await deleteCustomProduct(id); toast.success('Deleted'); fetch(); }
        catch { toast.error('Failed to delete'); }
    };

    return (
        <div className="space-y-5" data-testid="custom-products-page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Custom Products</h1>
                    <p className="text-muted-foreground text-sm mt-1">{products.length} products</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { window.open(exportCustomProducts(), '_blank'); }} data-testid="export-custom-products-btn">
                        <DownloadSimple size={16} className="mr-2" />Export CSV
                    </Button>
                    <Button asChild data-testid="new-custom-product-btn">
                        <Link to="/custom-products/new"><Plus size={18} className="mr-2" />New Custom Product</Link>
                    </Button>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search code, name, customer..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
                </div>
                <Select value={statusFilter || "all"} onValueChange={v => setStatusFilter(v === "all" ? '' : v)}>
                    <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Status" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        {Object.entries(statusConfig).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Product Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Decor</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Updated</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? Array(5).fill(0).map((_, i) => (
                            <TableRow key={i}>{Array(9).fill(0).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                        )) : products.length === 0 ? (
                            <TableRow><TableCell colSpan={9} className="h-32 text-center">
                                <PuzzlePiece size={36} className="mx-auto mb-2 text-muted-foreground/40" />
                                <p className="text-muted-foreground">No custom products yet</p>
                            </TableCell></TableRow>
                        ) : products.map(p => {
                            const sc = statusConfig[p.status] || statusConfig.inquiry;
                            return (
                                <TableRow key={p.id}>
                                    <TableCell className="font-mono font-semibold text-sm">{p.custom_code}</TableCell>
                                    <TableCell>{p.product_name || '—'}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{typeLabels[p.custom_type] || p.custom_type}</TableCell>
                                    <TableCell className="text-sm">{p.customer_name || '—'}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{p.category || '—'}</TableCell>
                                    <TableCell className="font-mono text-sm">{p.decor_no || '—'}</TableCell>
                                    <TableCell><Badge className={`${sc.color} text-xs`}>{sc.label}</Badge></TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{formatDate(p.updated_at)}</TableCell>
                                    <TableCell>
                                        <div className="flex gap-1">
                                            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                                                <Link to={`/custom-products/${p.id}/edit`}><PencilSimple size={15} /></Link>
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(p.id)}>
                                                <Trash size={15} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
